// The host self-check at /host-check (host/checks.ts): phase 0 of `design-docs/Windows app — design.md`, read in the
// window it's about. The automatic checks run on opening; the passkey trial and the items below need someone at the
// keyboard; Copy Report puts everything on the clipboard as text to send back. Signed in or not.
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { automaticChecks, passkeyTrial, report, type CheckResult } from '../host/checks.ts';
import { saveFile } from '../host/host.ts';
import { Masthead } from '../shell/Gate.tsx';
import type { Account } from '../store/account.ts';

const BY_HAND: { id: string; question: string; how: string }[] = [
  { id: 'external', question: 'Did the link open in your web browser, not in this window?', how: 'Choose Open fiverooms.app below.' },
  { id: 'save', question: 'Did a Save dialog ask where to put the file?', how: 'Choose Save a Test File below.' },
  { id: 'close', question: 'Closing straight after typing: did the window wait, or warn about unsent changes?', how: 'Open a project, type a word, and close the window at once.' },
  { id: 'offline', question: 'Offline: did the app open with your projects?', how: 'Quit, turn off the network, open Five Rooms again.' },
  { id: 'invite', question: 'Did an invitation link open in the app?', how: 'Choose a writingsuite:// or create.fiverooms.app/share link in another app.' },
];

const ANSWERS = ['Yes', 'No', 'Not tried'] as const;

function Results({ results }: { results: CheckResult[] }) {
  return (
    <ul className="host-check-list">
      {results.map((r) => (
        <li key={r.id} data-check={r.id} data-status={r.status}>
          <span className={`host-check-mark ${r.status}`} aria-label={r.status}>
            {r.status === 'pass' ? '✓' : r.status === 'fail' ? '✗' : '•'}
          </span>
          <span>
            <strong>{r.label}</strong> <span className="host-check-detail">{r.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function HostCheck({ account }: { account: Account | null }) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [automatic, setAutomatic] = useState<CheckResult[] | null>(null);
  const [trial, setTrial] = useState<CheckResult[]>([]);
  const [trying, setTrying] = useState(false);
  const [page, setPage] = useState<CheckResult[]>([]);
  const [keys, setKeys] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'Host check — Five Rooms';
    heading.current?.focus();
    void automaticChecks().then(setAutomatic);
  }, []);

  const note = (r: CheckResult) => setPage((list) => [...list.filter((x) => x.id !== r.id), r]);

  const tryPasskey = async () => {
    setTrying(true);
    try {
      setTrial((await passkeyTrial()).steps);
    } finally {
      setTrying(false);
    }
  };

  const save = async () => {
    const answer = await saveFile('Five Rooms host check.txt', new TextEncoder().encode('A test file from the Five Rooms host check.\n'), 'text/plain');
    note({
      id: 'saveResult',
      label: 'Save a test file',
      status: answer === 'downloaded' ? 'fail' : 'pass',
      detail: answer === 'downloaded' ? 'downloaded without a dialog (the shell’s Save wasn’t used)' : answer,
    });
  };

  const dropped = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = [...e.dataTransfer.files].map((f) => f.name);
    const text = e.dataTransfer.getData('text/plain');
    note({
      id: 'drop',
      label: 'Drag and drop reaches the page',
      status: files.length || text ? 'pass' : 'fail',
      detail: files.length ? `files: ${files.join(', ')}` : text ? `text: “${text.slice(0, 40)}”` : 'nothing arrived',
    });
  };

  const keyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (['Control', 'Shift', 'Alt', 'Meta'].includes(e.key)) return; // a modifier on its own: wait for the key it goes with
    if (!e.ctrlKey && !e.metaKey && !e.altKey && !/^F\d+$/.test(e.key)) return;
    e.preventDefault();
    const combo = [e.ctrlKey && 'Ctrl', e.metaKey && 'Meta', e.altKey && 'Alt', e.shiftKey && 'Shift', e.key.length === 1 ? e.key.toUpperCase() : e.key].filter(Boolean).join('+');
    setKeys((list) => (list.includes(combo) ? list : [...list, combo]));
  };

  const copy = async () => {
    const answered = Object.fromEntries(BY_HAND.filter((q) => answers[q.id]).map((q) => [q.question, answers[q.id]!]));
    const keysResult: CheckResult[] = keys.length ? [{ id: 'keys', label: 'Keys that reached the page', status: 'info', detail: keys.join(', ') }] : [];
    const text = report(
      [
        { title: 'Automatic', results: automatic ?? [] },
        { title: 'Passkey trial', results: trial },
        { title: 'On this page', results: [...page, ...keysResult] },
      ],
      answered,
    );
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2_000);
  };

  return (
    <div className="calm-page">
      <Masthead />
      <main className="calm host-check" data-state="host-check">
        <p className="settings-back">
          <a href="/">← {account ? 'Back to the Library' : 'Back'}</a>
        </p>
        <h1 ref={heading} tabIndex={-1}>
          Host check
        </h1>
        <p className="lede">
          What this browser or app can do for Five Rooms. Nothing here is sent anywhere: when you’re done, copy the report and send it.
        </p>

        <section className="settings-section" aria-labelledby="host-check-automatic">
          <h2 id="host-check-automatic" className="eyebrow">
            Automatic
          </h2>
          {automatic ? <Results results={automatic} /> : <p className="status-note">Checking…</p>}
        </section>

        <section className="settings-section" aria-labelledby="host-check-passkey">
          <h2 id="host-check-passkey" className="eyebrow">
            Passkey trial
          </h2>
          <p>
            Makes a passkey called “five-rooms-test (delete me)” and asks it twice for the key that opens the vault, as signing up does. Choose Windows
            Hello, your phone or a security key when asked.
          </p>
          <p className="notice">
            Delete it when you’re done (on Windows: Settings ▸ Accounts ▸ Passkeys). Until then it’s offered whenever you sign in to Five Rooms, and
            it can’t sign you in.
          </p>
          <p className="actions">
            <button type="button" className="pill" onClick={() => void tryPasskey()} disabled={trying}>
              {trying ? 'Waiting for the passkey…' : 'Try a Passkey'}
            </button>
          </p>
          {trial.length ? <Results results={trial} /> : null}
        </section>

        <section className="settings-section" aria-labelledby="host-check-page">
          <h2 id="host-check-page" className="eyebrow">
            On this page
          </h2>
          <p className="actions">
            <a className="pill outline" href="https://fiverooms.app/" target="_blank" rel="noopener noreferrer">
              Open fiverooms.app
            </a>{' '}
            <button type="button" className="pill outline" onClick={() => void save()}>
              Save a Test File
            </button>
          </p>
          <div className="host-check-drop" onDragOver={(e) => e.preventDefault()} onDrop={dropped} data-drop>
            Drop a file here, or drag <span draggable onDragStart={(e) => e.dataTransfer.setData('text/plain', 'these words')}>these words</span> onto it.
          </div>
          <label className="host-check-keys">
            Press Ctrl+P, Ctrl+F, Ctrl+Shift+I, F7 and Ctrl+Alt+S in this field:
            <input type="text" readOnly onKeyDown={keyDown} value={keys.join('  ')} placeholder="Keys the page receives appear here" />
          </label>
          {page.length ? <Results results={page} /> : null}
        </section>

        <section className="settings-section" aria-labelledby="host-check-hand">
          <h2 id="host-check-hand" className="eyebrow">
            By hand
          </h2>
          <ul className="host-check-list">
            {BY_HAND.map((q) => (
              <li key={q.id}>
                <span>
                  <strong>{q.question}</strong> <span className="host-check-detail">{q.how}</span>
                </span>
                <select aria-label={q.question} value={answers[q.id] ?? ''} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}>
                  <option value="">—</option>
                  {ANSWERS.map((a) => (
                    <option key={a}>{a}</option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </section>

        <p className="actions">
          <button type="button" className="pill" onClick={() => void copy()} disabled={!automatic}>
            {copied ? 'Copied' : 'Copy Report'}
          </button>
        </p>
      </main>
    </div>
  );
}
